/**
 * Sidebar — asosiy navigatsiya (AvtoFix Design System)
 *
 * Spec:
 *   - Width: 232px (Design System)
 *   - bg-sidebar (oq light, deepest dark)
 *   - Brand satr: gear logo + "AvtoFix" wordmark, "POS" badge
 *   - Section overline (uppercase, letter-spacing) — Asosiy / Boshqaruv / Vositalar
 *   - Nav item: 36px height, soft-red active background, left red bar
 *   - Profil pastida — neutral avatar, role pill
 *   - Impersonation: "O'zimnikiga qaytish" tugmasi profil ustida
 *
 * Mobile: lg: dan kichikda overlay sifatida, open/onClose props orqali boshqariladi.
 */

import type { UserRole } from '@avtofix/shared';
import {
  ArrowLeftCircle,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FolderTree,
  Home,
  Image,
  Loader2,
  MapPin,
  Package,
  PackageCheck,
  ShoppingBag,
  ShoppingCart,
  Store,
  Ticket,
  Truck,
  UserCheck,
  UserCog,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import { useUiStore } from '@/app/stores/ui.store';
import {
  useCurrentUser,
  useIsImpersonating,
  useStopImpersonation,
} from '@/features/auth/auth.hooks';
import { useStore } from '@/features/stores/stores.hooks';
import { ApiClientError } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';
import { toast } from '@/shared/ui/toast';

type NavKey =
  | 'nav.dashboard'
  | 'nav.kassa'
  | 'nav.orderRequests'
  | 'nav.specialOrders'
  | 'nav.products'
  | 'nav.categories'
  | 'nav.suppliers'
  | 'nav.customers'
  | 'nav.debts'
  | 'nav.debtCategories'
  | 'nav.stats'
  | 'nav.roles'
  | 'nav.users'
  | 'nav.xodimlar'
  | 'nav.stores'
  | 'nav.marketplaceOrders'
  | 'nav.pickupPoints'
  | 'nav.promoCodes'
  | 'nav.marketplaceBanners'
  | 'nav.pickupOperator'
  | 'nav.courier'
  | 'nav.courierTracking';

type NavSection = 'main' | 'marketplace' | 'admin';

interface NavItem {
  to: string;
  icon: LucideIcon;
  labelKey: NavKey;
  roles: UserRole[];
  section: NavSection;
  /** Strelka (chevron) bosilganda ochiladigan sub-bo'limlar. */
  children?: { to: string; label: string }[];
}

const NAV_ITEMS: NavItem[] = [
  // ─── Asosiy ish ekrani ──────────────────────────────────────────
  {
    to: '/',
    icon: Home,
    labelKey: 'nav.dashboard',
    roles: ['EGASI', 'ADMIN', 'XODIM'],
    section: 'main',
  },
  {
    to: '/kassa',
    icon: ShoppingCart,
    labelKey: 'nav.kassa',
    roles: ['EGASI', 'ADMIN', 'XODIM'],
    section: 'main',
  },
  {
    to: '/buyurtmalar',
    icon: ClipboardList,
    labelKey: 'nav.orderRequests',
    roles: ['EGASI', 'ADMIN', 'XODIM'],
    section: 'main',
  },
  {
    to: '/yangi-buyurtmalar',
    icon: ClipboardList,
    labelKey: 'nav.specialOrders',
    roles: ['EGASI', 'ADMIN', 'XODIM'],
    section: 'main',
    children: [
      { to: '/mijoz-sorovi', label: "Mijoz so'rovi" },
      { to: '/narx-aytildi', label: 'Narx aytildi' },
    ],
  },
  {
    to: '/marketplace-orders',
    icon: ShoppingBag,
    labelKey: 'nav.marketplaceOrders',
    roles: ['EGASI', 'ADMIN'],
    section: 'marketplace',
  },
  {
    to: '/pickup-points',
    icon: MapPin,
    labelKey: 'nav.pickupPoints',
    roles: ['EGASI'],
    section: 'marketplace',
  },
  {
    to: '/promo-codes',
    icon: Ticket,
    labelKey: 'nav.promoCodes',
    roles: ['EGASI'],
    section: 'marketplace',
  },
  {
    to: '/marketplace/bannerlar',
    icon: Image,
    labelKey: 'nav.marketplaceBanners',
    roles: ['EGASI'],
    section: 'marketplace',
  },
  {
    to: '/pickup-operator',
    icon: PackageCheck,
    labelKey: 'nav.pickupOperator',
    roles: ['PICKUP_OPERATOR'],
    section: 'main',
  },
  {
    to: '/courier',
    icon: Truck,
    labelKey: 'nav.courier',
    roles: ['COURIER'],
    section: 'main',
  },
  {
    to: '/courier-tracking',
    icon: MapPin,
    labelKey: 'nav.courierTracking',
    roles: ['EGASI'],
    section: 'marketplace',
  },
  {
    to: '/products',
    icon: Package,
    labelKey: 'nav.products',
    roles: ['EGASI', 'ADMIN', 'XODIM'],
    section: 'main',
  },
  {
    to: '/categories',
    icon: FolderTree,
    labelKey: 'nav.categories',
    roles: ['EGASI', 'ADMIN'],
    section: 'main',
  },
  {
    to: '/suppliers',
    icon: Truck,
    labelKey: 'nav.suppliers',
    roles: ['EGASI', 'ADMIN'],
    section: 'main',
  },
  {
    to: '/customers',
    icon: Users,
    labelKey: 'nav.customers',
    roles: ['EGASI', 'ADMIN', 'XODIM'],
    section: 'main',
  },
  {
    to: '/debts',
    icon: CreditCard,
    labelKey: 'nav.debts',
    roles: ['EGASI', 'ADMIN'],
    section: 'main',
  },
  // Qarz kategoriyalari sidebar'dan olib tashlandi — endi qarz muddatini
  // uzaytirganda "N kunlik" kategoriya AVTOMATIK yaratiladi/biriktiriladi
  // (DebtCard → DueDateInline). Sahifa/route saqlanadi (kategoriya detali uchun).

  // ─── Boshqaruv ───────────────────────────────────────────────────
  {
    to: '/stats',
    icon: BarChart2,
    labelKey: 'nav.stats',
    roles: ['EGASI', 'ADMIN'],
    section: 'admin',
  },
  {
    to: '/rollar',
    icon: UserCog,
    labelKey: 'nav.roles',
    roles: ['EGASI', 'ADMIN'],
    section: 'admin',
  },
  {
    to: '/users',
    icon: Users,
    labelKey: 'nav.users',
    roles: ['EGASI', 'ADMIN'],
    section: 'admin',
  },
  {
    to: '/xodimlar',
    icon: UserCheck,
    labelKey: 'nav.xodimlar',
    roles: ['EGASI', 'ADMIN'],
    section: 'admin',
  },
  {
    to: '/stores',
    icon: Store,
    labelKey: 'nav.stores',
    roles: ['EGASI'],
    section: 'admin',
  },
];

const SECTION_LABELS: Record<NavSection, string> = {
  main: 'Asosiy',
  marketplace: 'Marketplace',
  admin: 'Boshqaruv',
};

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps): React.ReactElement {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const userRole = user?.role;
  const isImpersonating = useIsImpersonating();
  const stopImpersonation = useStopImpersonation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const location = useLocation();

  // Sub-bo'limli item (Yangi buyurtmalar) ochiq/yopiq holati. Joriy yo'l
  // sub-bo'limда bo'lsa — avtomatik ochiq.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = new Set<string>();
    for (const it of NAV_ITEMS) {
      if (it.children?.some((c) => c.to === location.pathname) === true) s.add(it.to);
    }
    return s;
  });
  const toggleExpand = (to: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(to)) next.delete(to);
      else next.add(to);
      return next;
    });
  };

  // ADMIN "Marketplace buyurtmalari"ni faqat do'koni marketplace'da bo'lsa ko'radi —
  // do'kon EGASI tomonidan marketplace'ga qo'shilgandan keyin bo'lim ochiladi.
  const isAdmin = userRole === 'ADMIN';
  const { data: adminStore } = useStore(isAdmin ? (user?.storeId ?? undefined) : undefined);
  const adminStoreApproved = adminStore?.marketplaceStatus === 'APPROVED';

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (userRole === undefined || !item.roles.includes(userRole)) return false;
    if (item.to === '/marketplace-orders' && isAdmin && !adminStoreApproved) return false;
    return true;
  });

  const sections = (['main', 'marketplace', 'admin'] as const)
    .map((section) => ({
      section,
      items: visibleItems.filter((i) => i.section === section),
    }))
    .filter((s) => s.items.length > 0);

  const handleStopImpersonation = (): void => {
    stopImpersonation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Asl hisobingizga qaytingiz');
        onClose?.();
      },
      onError: (err) => {
        toast.error(err instanceof ApiClientError ? err.message : 'Xato');
      },
    });
  };

  const navContent = (isCollapsed: boolean): React.ReactElement => (
    <>
      {/* Brand satr */}
      <div
        className={cn(
          'flex h-[60px] shrink-0 items-center gap-2.5 border-b border-border',
          isCollapsed ? 'justify-center px-2' : 'px-4',
        )}
      >
        {isCollapsed ? (
          <BrandMark />
        ) : (
          <>
            <img
              src="/logo.png"
              alt="AvtoFix"
              className="h-9 w-auto select-none"
              draggable={false}
            />
            <span className="ml-auto rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              POS
            </span>
          </>
        )}
      </div>

      {/* Navigatsiya */}
      <nav
        className={cn(
          'scrollbar-none flex flex-1 flex-col gap-0.5 overflow-y-auto py-3',
          isCollapsed ? 'px-2' : 'px-2.5',
        )}
        aria-label="Asosiy navigatsiya"
      >
        {sections.map(({ section, items }, idx) => (
          <div key={section} className={cn(idx > 0 && 'mt-3')}>
            {!isCollapsed && (
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {SECTION_LABELS[section]}
              </div>
            )}
            {isCollapsed && idx > 0 && (
              <div aria-hidden className="mx-2 my-1.5 h-px bg-border/60" />
            )}
            {items.map((item) => {
              const Icon = item.icon;
              const hasChildren =
                item.children !== undefined && item.children.length > 0 && !isCollapsed;
              const isExpanded = expanded.has(item.to);
              const navLink = (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={onClose}
                  title={isCollapsed ? t(item.labelKey) : undefined}
                  className={({ isActive }: { isActive: boolean }) =>
                    cn(
                      'group relative flex h-11 flex-1 items-center gap-3 rounded-md text-[13px] font-medium transition-colors duration-150 ease-out lg:h-9',
                      isCollapsed ? 'justify-center px-0' : 'px-3',
                      hasChildren && 'pr-9',
                      isActive
                        ? 'bg-primary-soft font-semibold text-primary'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                    )
                  }
                >
                  {({ isActive }: { isActive: boolean }) => (
                    <>
                      {isActive && !isCollapsed && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary"
                        />
                      )}
                      <Icon className="size-[17px] shrink-0" strokeWidth={1.75} aria-hidden />
                      {!isCollapsed && <span className="truncate">{t(item.labelKey)}</span>}
                    </>
                  )}
                </NavLink>
              );
              if (!hasChildren) return navLink;
              return (
                <div key={item.to}>
                  <div className="relative flex items-center">
                    {navLink}
                    <button
                      type="button"
                      onClick={() => {
                        toggleExpand(item.to);
                      }}
                      aria-label={isExpanded ? 'Yopish' : 'Ochish'}
                      aria-expanded={isExpanded}
                      className="absolute right-1 z-10 flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                    >
                      <ChevronRight
                        className={cn('size-4 transition-transform', isExpanded && 'rotate-90')}
                        strokeWidth={2}
                      />
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="mt-0.5 ml-7 space-y-0.5 border-l border-border/60 pl-2">
                      {(item.children ?? []).map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={onClose}
                          className={({ isActive }: { isActive: boolean }) =>
                            cn(
                              'flex h-9 items-center rounded-md px-3 text-[13px] font-medium transition-colors',
                              isActive
                                ? 'bg-primary-soft font-semibold text-primary'
                                : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                            )
                          }
                        >
                          <span className="truncate">{c.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Impersonation banner — faqat boshqa user sifatida kirilgan bo'lsa.
          Profil bloki olib tashlandi (Navbar dropdown'ga ko'chirildi). */}
      {user !== null && isImpersonating && (
        <div className="shrink-0 border-t border-border p-2.5">
          <button
            type="button"
            onClick={handleStopImpersonation}
            disabled={stopImpersonation.isPending}
            title={isCollapsed ? "O'zimnikiga qaytish" : undefined}
            className={cn(
              'flex w-full items-center justify-center rounded-md text-[12.5px] font-semibold transition-colors',
              isCollapsed ? 'h-9 px-0' : 'gap-2 px-3 py-2',
              'bg-info/10 text-info hover:bg-info/20',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {stopImpersonation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <ArrowLeftCircle className="size-4" aria-hidden />
            )}
            {!isCollapsed && <span>O&apos;zimnikiga qaytish</span>}
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Desktop sidebar — 232px (full) yoki 64px (collapsed) */}
      <aside
        className={cn(
          'relative hidden shrink-0 flex-col border-r border-border bg-sidebar transition-[width] duration-200 ease-out lg:flex',
          collapsed ? 'w-16' : 'w-[232px]',
        )}
      >
        {navContent(collapsed)}

        {/* Sidebar collapse/expand tugmasi — chap chetda yopishgan */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={collapsed ? 'Sidebar ochish' : 'Sidebar yopish'}
          title={collapsed ? 'Sidebar ochish' : 'Sidebar yopish'}
          className={cn(
            'group absolute -right-3 top-20 z-10 hidden lg:flex',
            'size-6 items-center justify-center rounded-full',
            'border border-border bg-card text-muted-foreground shadow-md',
            'transition-all hover:scale-110 hover:border-primary hover:text-primary',
          )}
        >
          {collapsed ? (
            <ChevronRight className="size-3.5" strokeWidth={2.5} />
          ) : (
            <ChevronLeft className="size-3.5" strokeWidth={2.5} />
          )}
        </button>
      </aside>

      {/* Mobile sidebar (overlay) — har doim full width */}
      {open === true && (
        <>
          <button
            type="button"
            onClick={onClose}
            className="z-overlay-sidebar fixed inset-0 bg-foreground/40 backdrop-blur-sm duration-200 animate-in fade-in-0 lg:hidden"
            aria-label="Yopish"
            tabIndex={-1}
          />
          <aside className="z-sidebar fixed inset-y-0 left-0 flex w-[260px] flex-col border-r border-border bg-sidebar pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl duration-200 ease-out animate-in slide-in-from-left lg:hidden">
            {navContent(false)}
          </aside>
        </>
      )}
    </>
  );
}

/**
 * BrandMark — Soddalashtirilgan AvtoFix logo (gear + qizil aksent).
 * Keyinchalik haqiqiy SVG bilan almashtirish mumkin.
 */
function BrandMark(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 28 28"
      className="size-7 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {/* Gear outer */}
      <path
        d="M14 2.5l1.6 2.6 3 .4 1.5 2.6-1.4 2.7 1.4 2.7-1.5 2.6-3 .4-1.6 2.6h-.0l-1.6-2.6-3-.4-1.5-2.6 1.4-2.7-1.4-2.7 1.5-2.6 3-.4 1.6-2.6z"
        className="text-muted-foreground"
      />
      {/* Inner red dot — brand accent */}
      <circle cx="14" cy="11" r="3.2" className="text-primary" fill="currentColor" stroke="none" />
    </svg>
  );
}
